import Box from '@mui/material/Box';
import { ComponentType } from 'react';

import { ButtonWithPopover } from '@/components/form/baseInputs/Button/ButtonWithPopover';
import { SelectItemGroupLabel } from '@/components/form/baseInputs/SelectInput/SelectItemGroupLabel';
import { CheckIcon } from '@/components/icons/CheckIcon';
import { IconProps } from '@/components/icons/CircleIcon';
import { MenuItem } from '@/components/poppers/MenuPopper/MenuItem';
import { GraphVizTileEmphasis } from '@/modules/graphViz/graphVizNodeConfig';
import { COLORS } from '@/styles/tokens/colors';

/**
 * Inline emphasis icons. All three icons share the same outlined-tile-with-
 * filled-left-stripe shape (per the Figma design) and differ only in the body
 * fill opacity — Low has a transparent body, Medium a light tint, and High a
 * deeper tint. Stroke and stripe use `currentColor` so the icons inherit
 * MUI's theme color at the call site.
 */

function EmphasisIconFrame({
  size,
  sx,
  children,
}: {
  size: number;
  sx?: IconProps['sx'];
  children: React.ReactNode;
}) {
  return (
    <Box
      component="span"
      sx={{
        height: size,
        width: size,
        display: 'inline-block',
        overflow: 'hidden',
        flexShrink: 0,
        userSelect: 'none',
        textAlign: 'center',
        ...sx,
      }}
    >
      <svg
        width={size}
        height={size}
        viewBox="0 0 16 16"
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
      >
        {children}
      </svg>
    </Box>
  );
}

// Rounded-left-corner stripe path copied from the Figma source (node 5:15258).
const STRIPE_PATH =
  'M0 3C0 2.44772 0.447715 2 1 2L3 2V14H1C0.447715 14 0 13.5523 0 13V3Z';

function EmphasisTileShape({ bodyFillOpacity }: { bodyFillOpacity: number }) {
  return (
    <>
      <rect
        x="0.5"
        y="2.5"
        width="15"
        height="11"
        rx="0.5"
        stroke="currentColor"
        strokeWidth="1"
        fill="currentColor"
        fillOpacity={bodyFillOpacity}
      />
      <path d={STRIPE_PATH} fill="currentColor" />
    </>
  );
}

function EmphasisLowIcon({ size = 16, sx }: IconProps) {
  return (
    <EmphasisIconFrame size={size} sx={sx}>
      <EmphasisTileShape bodyFillOpacity={0} />
    </EmphasisIconFrame>
  );
}

function EmphasisMediumIcon({ size = 16, sx }: IconProps) {
  return (
    <EmphasisIconFrame size={size} sx={sx}>
      <EmphasisTileShape bodyFillOpacity={0.15} />
    </EmphasisIconFrame>
  );
}

function EmphasisHighIcon({ size = 16, sx }: IconProps) {
  return (
    <EmphasisIconFrame size={size} sx={sx}>
      <EmphasisTileShape bodyFillOpacity={0.35} />
    </EmphasisIconFrame>
  );
}

interface EmphasisOption {
  label: string;
  value: GraphVizTileEmphasis;
  Icon: ComponentType<IconProps>;
}

const EMPHASIS_OPTIONS: EmphasisOption[] = [
  { label: 'Low', value: GraphVizTileEmphasis.Low, Icon: EmphasisLowIcon },
  {
    label: 'Medium',
    value: GraphVizTileEmphasis.Medium,
    Icon: EmphasisMediumIcon,
  },
  { label: 'High', value: GraphVizTileEmphasis.High, Icon: EmphasisHighIcon },
];

const ICON_SIZE = 16;

interface TileEmphasisPickerProps {
  currentEmphasis: GraphVizTileEmphasis;
  /** When true, multiple different emphases are selected — no checkmark shown */
  isMixed?: boolean;
  onEmphasisChange: (emphasis: GraphVizTileEmphasis) => void;
  disabled?: boolean;
}

export function TileEmphasisPicker({
  currentEmphasis,
  isMixed = false,
  onEmphasisChange,
  disabled = false,
}: TileEmphasisPickerProps) {
  const CurrentEmphasisIcon =
    EMPHASIS_OPTIONS.find((opt) => opt.value === currentEmphasis)?.Icon ??
    EmphasisLowIcon;
  const showCheck = !isMixed;

  return (
    <ButtonWithPopover
      variant="transparent"
      size="xs"
      disabled={disabled}
      label={<CurrentEmphasisIcon size={ICON_SIZE} />}
      highlightWhenOpen
      popperProps={{
        anchorOrigin: { horizontal: 'left', vertical: 'bottom' },
        transformOrigin: { horizontal: 'left', vertical: 'top' },
        PaperProps: { sx: { minWidth: 160 } },
      }}
    >
      <SelectItemGroupLabel label="Contrast" />
      {EMPHASIS_OPTIONS.map(({ label, value, Icon }) => {
        const isActive = showCheck && value === currentEmphasis;
        return (
          <MenuItem
            key={value}
            icon={<Icon size={ICON_SIZE} />}
            label={label}
            iconAfter={
              isActive ? (
                <CheckIcon size={ICON_SIZE} color={COLORS.TEAL[600]} />
              ) : undefined
            }
            onClick={() => onEmphasisChange(value)}
          />
        );
      })}
    </ButtonWithPopover>
  );
}
